/**
 * Complete the Set -- PDP Add-on Widget
 * Vanilla JS, no dependencies.
 *
 * Variant data is embedded server-side via data-variants on each .cts-item.
 * Default variant is pre-selected on first check for 1-click add.
 * Hooks into the theme's native add-to-cart form to bundle checked add-ons.
 */

(function () {
  'use strict';

  var SECTION_SEL = '.cts-section';
  var ITEM_SEL = '.cts-item';

  /* ========== Init ========== */

  function init() {
    var section = document.querySelector(SECTION_SEL);
    if (!section) return;

    // Relocate widget into the product info column if anchor exists
    // (may already be relocated by the inline script for faster paint)
    var anchor = document.getElementById('cts-anchor');
    if (anchor && section.parentNode !== anchor) {
      var wrapper = section.closest('.cts-section-wrapper');
      anchor.appendChild(section);
      if (wrapper) wrapper.style.display = 'none';
    }

    // Reveal section (hidden initially to prevent flash during relocation)
    section.style.opacity = '1';

    // Parse embedded variant data and set up defaults
    section.querySelectorAll(ITEM_SEL).forEach(function (item) {
      try {
        item._variants = JSON.parse(item.dataset.variants || '[]');
      } catch (e) {
        item._variants = [];
      }
      item._selectedOptions = {};

      // Pre-populate selected options from default-selected buttons
      item.querySelectorAll('.cts-variant-btn[aria-pressed="true"], .cts-swatch[aria-pressed="true"]').forEach(function (btn) {
        var group = btn.closest('.cts-option-group');
        if (group) {
          var optName = group.dataset.optionName;
          item._selectedOptions[optName] = btn.dataset.value || btn.textContent.trim();
        }
      });

      // Set default variant ID from pre-selected buttons
      if (item.dataset.defaultVariantId) {
        item.dataset.selectedVariantId = item.dataset.defaultVariantId;
      }
    });

    // Single capture-phase handler on section to intercept all clicks
    // before the theme's listeners can interfere
    section.addEventListener('click', function (e) {
      var qtyBtn = e.target.closest('.cts-qty-btn');
      if (qtyBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleQtyChange(qtyBtn);
        return;
      }

      var removeBtn = e.target.closest('.cts-remove-btn');
      if (removeBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleRemove(removeBtn.closest(ITEM_SEL));
        return;
      }

      var addBtn = e.target.closest('.cts-add-btn');
      if (addBtn) {
        e.preventDefault();
        e.stopImmediatePropagation();
        handleSeparateAdd(addBtn.closest(ITEM_SEL), addBtn);
        return;
      }

      var swatch = e.target.closest('.cts-swatch');
      if (swatch) {
        e.stopImmediatePropagation();
        handleSwatchSelect(swatch);
        return;
      }

      var variantBtn = e.target.closest('.cts-variant-btn');
      if (variantBtn) {
        e.stopImmediatePropagation();
        handleVariantSelect(variantBtn);
        return;
      }

      // Click a product image (row thumb or expanded thumb) -> open zoom lightbox.
      // Intercept before the row toggle so it doesn't also expand/collapse the item.
      var zoomThumb = e.target.closest('.cts-row-thumb, .cts-thumb');
      if (zoomThumb) {
        e.preventDefault();
        e.stopImmediatePropagation();
        var zImg = zoomThumb.querySelector('img');
        if (zImg && zImg.src) openZoom(zImg.src, zImg.alt || '');
        return;
      }

      var row = e.target.closest('.cts-row');
      if (row) {
        e.stopImmediatePropagation();
        handleToggle(row.closest(ITEM_SEL));
        return;
      }
    }, true);

    // Hook into the theme's native product form to bundle add-ons — bundle mode only.
    // In separate mode each item is added on its own via its Add button.
    if (!section.hasAttribute('data-cts-separate')) {
      hookProductForm(section);
    }

    // Init button total display
    initButtonTotal(section);
  }

  /* ========== Pre-order stamping ========== */

  // The section Liquid flags variants sold as genuine pre-orders with
  // "preorder": true in the embedded JSON (products carrying the
  // custom.preorder_date metafield or a preorder tag — plain backorder
  // products are never flagged). Those adds must carry the
  // properties[Pre-order] line-item property, because the cart
  // acknowledgement tick box only renders for lines that have it.
  function getPreorderProps(item, variantId) {
    var vid = parseInt(variantId, 10);
    var v = (item._variants || []).find(function (vr) { return vr && vr.id === vid; });
    if (!v || !v.preorder) return null;
    return { 'Pre-order': item.dataset.preorderNote || 'Ships when stock arrives' };
  }

  /* ========== Separate add-on: add a single item directly ========== */

  function handleSeparateAdd(item, btn) {
    if (!item || item.dataset.soldOut === 'true') return;
    // Resolve the selected (or default) variant
    if (!item._matchedVariant) resolveVariant(item);
    var variantId = item.dataset.selectedVariantId || item.dataset.defaultVariantId;
    if (!variantId) {
      // Options not fully chosen yet — expand so the customer can pick.
      if (item.dataset.checked !== 'true') handleToggle(item);
      return;
    }
    var qtyInput = item.querySelector('.cts-qty-value');
    var qty = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    if (btn.dataset.adding === '1') return;
    btn.dataset.adding = '1';
    var orig = btn.textContent;
    btn.textContent = 'Adding…';
    var addItem = { id: parseInt(variantId, 10), quantity: qty };
    var poProps = getPreorderProps(item, variantId);
    if (poProps) addItem.properties = poProps;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [addItem] })
    }).then(function (r) {
      if (!r.ok) throw new Error('add_failed');
      return r.json();
    }).then(function () {
      btn.textContent = 'Added ✓';
      triggerCartUpdate();
      setTimeout(function () { btn.textContent = orig; btn.dataset.adding = '0'; }, 1800);
    }).catch(function () {
      btn.textContent = orig;
      btn.dataset.adding = '0';
    });
  }

  /* ========== Toggle Checkbox ========== */

  function handleToggle(item) {
    if (!item || item.dataset.soldOut === 'true') return;

    var isChecked = item.dataset.checked === 'true';
    item.dataset.checked = isChecked ? 'false' : 'true';

    // On first check, resolve the pre-selected default variant
    if (!isChecked) {
      resolveVariant(item);
      if (!item._fetched) fetchLiveData(item);
    }
    var section = item.closest(SECTION_SEL);
    if (section) updateButtonTotal(section);
  }

  /* ========== Remove (uncheck + reset) ========== */

  function handleRemove(item) {
    if (!item) return;
    item.dataset.checked = 'false';
    item._selectedOptions = {};
    item.dataset.selectedVariantId = '';

    // Reset all buttons to default state
    item.querySelectorAll('.cts-variant-btn, .cts-swatch').forEach(function (b) {
      var isDefault = b.classList.contains('cts-variant-btn--default') || b.classList.contains('cts-swatch--default');
      b.setAttribute('aria-pressed', isDefault ? 'true' : 'false');
    });

    // Re-populate defaults
    item.querySelectorAll('.cts-variant-btn[aria-pressed="true"], .cts-swatch[aria-pressed="true"]').forEach(function (btn) {
      var group = btn.closest('.cts-option-group');
      if (group) {
        var optName = group.dataset.optionName;
        item._selectedOptions[optName] = btn.dataset.value || btn.textContent.trim();
        var label = group.querySelector('.cts-option-label span');
        if (label) label.textContent = '\u2014 ' + (btn.dataset.value || btn.textContent.trim());
      }
    });

    // Reset price display
    var promptEl = item.querySelector('.cts-choose-prompt');
    var priceEl = item.querySelector('.cts-selected-variant-price');
    var unitEl = item.querySelector('.cts-unit-price');
    var stockEl = item.querySelector('.cts-stock');
    showPrompt(promptEl, priceEl, unitEl, stockEl);

    // Reset qty
    var qtyInput = item.querySelector('.cts-qty-value');
    if (qtyInput) qtyInput.value = '1';

    // Restore default variant ID
    if (item.dataset.defaultVariantId) {
      item.dataset.selectedVariantId = item.dataset.defaultVariantId;
    }
    var section = item.closest(SECTION_SEL);
    if (section) updateButtonTotal(section);
  }

  /* ========== Fetch Live Availability ========== */

  function fetchLiveData(item) {
    var handle = item.dataset.handle;
    if (!handle) return;
    item._fetched = true;

    fetch('/products/' + handle + '.js')
      .then(function (res) { return res.json(); })
      .then(function (product) {
        product.variants.forEach(function (liveV) {
          var embedded = item._variants.find(function (v) { return v.id === liveV.id; });
          if (embedded) embedded.available = liveV.available;
        });

        item.querySelectorAll('.cts-variant-btn, .cts-swatch').forEach(function (btn) {
          var vid = parseInt(btn.dataset.variantId, 10);
          var v = item._variants.find(function (vr) { return vr.id === vid; });
          if (v && !v.available) btn.disabled = true;
        });
      })
      .catch(function () {});
  }

  /* ========== Variant Selection ========== */

  function handleVariantSelect(btn) {
    if (btn.disabled) return;

    var group = btn.closest('.cts-option-group');
    var optionName = group.dataset.optionName;
    var item = btn.closest(ITEM_SEL);

    group.querySelectorAll('.cts-variant-btn').forEach(function (b) {
      b.setAttribute('aria-pressed', 'false');
    });
    btn.setAttribute('aria-pressed', 'true');

    var label = group.querySelector('.cts-option-label span');
    if (label) label.textContent = '\u2014 ' + btn.textContent.trim();

    item._selectedOptions[optionName] = btn.textContent.trim();
    resolveVariant(item);
  }

  /* ========== Swatch Selection ========== */

  function handleSwatchSelect(swatch) {
    if (swatch.disabled) return;

    var group = swatch.closest('.cts-option-group');
    var optionName = group.dataset.optionName;
    var item = swatch.closest(ITEM_SEL);

    group.querySelectorAll('.cts-swatch').forEach(function (s) {
      s.setAttribute('aria-pressed', 'false');
    });
    swatch.setAttribute('aria-pressed', 'true');

    var label = group.querySelector('.cts-option-label span');
    if (label) label.textContent = '\u2014 ' + (swatch.title || swatch.dataset.value);

    item._selectedOptions[optionName] = swatch.dataset.value;
    resolveVariant(item);
  }

  /* ========== Resolve Variant ========== */

  function resolveVariant(item) {
    var selections = item._selectedOptions;
    var optionGroups = item.querySelectorAll('.cts-option-group');
    var totalOptions = optionGroups.length;
    var selectedCount = Object.keys(selections).length;

    var promptEl = item.querySelector('.cts-choose-prompt');
    var priceEl = item.querySelector('.cts-selected-variant-price');
    var unitEl = item.querySelector('.cts-unit-price');
    var stockEl = item.querySelector('.cts-stock');
    var thumbEl = item.querySelector('.cts-thumb img');

    if (selectedCount < totalOptions) {
      showPrompt(promptEl, priceEl, unitEl, stockEl);
      item.dataset.selectedVariantId = '';
      return;
    }

    var optionMap = {};
    optionGroups.forEach(function (g) {
      var pos = parseInt(g.dataset.optionPosition, 10);
      var name = g.dataset.optionName;
      optionMap[pos] = selections[name];
    });

    var matched = item._variants.find(function (v) {
      for (var pos in optionMap) {
        if (v['option' + pos] !== optionMap[pos]) return false;
      }
      return true;
    });

    if (matched) {
      // Store matched variant on item for qty-based price updates
      item._matchedVariant = matched;

      var qtyInput = item.querySelector('.cts-qty-value');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      var totalPrice = matched.price * qty;

      if (promptEl) promptEl.style.display = 'none';
      if (priceEl) {
        priceEl.textContent = formatMoney(totalPrice);
        priceEl.style.display = '';
      }
      if (unitEl) {
        var unitPrice = matched.unit_price;
        if (!unitPrice && matched.pack_qty && matched.pack_qty > 1) {
          unitPrice = Math.round(matched.price / matched.pack_qty);
        }
        if (unitPrice) {
          unitEl.textContent = formatMoney(unitPrice) + ' per unit';
          unitEl.style.display = '';
        } else {
          unitEl.style.display = 'none';
        }
      }
      if (stockEl) {
        stockEl.style.display = '';
        if (matched.available === false) {
          stockEl.className = 'cts-stock cts-stock--out-of-stock';
          stockEl.textContent = 'Out of stock';
        } else if (matched.preorder) {
          stockEl.className = 'cts-stock cts-stock--preorder';
          stockEl.textContent = 'Pre-order: ' + (item.dataset.preorderNote || 'ships when stock arrives');
        } else {
          stockEl.className = 'cts-stock cts-stock--in-stock';
          stockEl.textContent = 'In stock';
        }
      }

      item.dataset.selectedVariantId = matched.id;

      // Update collapsed row price to match selected variant × qty
      updateRowPrice(item, matched, qty);

      if (matched.image && thumbEl) {
        thumbEl.src = matched.image;
      }
    } else {
      item._matchedVariant = null;
      showPrompt(promptEl, priceEl, unitEl, stockEl);
      item.dataset.selectedVariantId = '';
    }
    var section = item.closest(SECTION_SEL);
    if (section) updateButtonTotal(section);
  }

  function updateRowPrice(item, variant, qty) {
    qty = qty || 1;
    var amountEl = item.querySelector('.cts-row-price-amount');
    var variantEl = item.querySelector('.cts-row-price-variant');
    if (amountEl) amountEl.textContent = formatMoney(variant.price * qty);
    if (variantEl) {
      // Build label from selected options
      var parts = [];
      item.querySelectorAll('.cts-option-group').forEach(function (g) {
        var selected = g.querySelector('[aria-pressed="true"]');
        if (selected) {
          parts.push(selected.dataset.value || selected.textContent.trim());
        }
      });
      var label = parts.length ? parts.join(' / ') : '';
      if (qty > 1) label += (label ? ' ' : '') + '× ' + qty;
      variantEl.textContent = label ? '/ ' + label : '';
    }
  }

  function showPrompt(promptEl, priceEl, unitEl, stockEl) {
    if (promptEl) promptEl.style.display = '';
    if (priceEl) priceEl.style.display = 'none';
    if (unitEl) unitEl.style.display = 'none';
    if (stockEl) stockEl.style.display = 'none';
  }

  /* ========== Quantity ========== */

  function handleQtyChange(btn) {
    var control = btn.closest('.cts-qty-control');
    var input = control.querySelector('.cts-qty-value');
    var val = parseInt(input.value, 10) || 1;
    var delta = btn.dataset.dir === 'up' ? 1 : -1;
    val = Math.max(1, Math.min(99, val + delta));
    input.value = val;

    // Update prices to reflect new qty
    var item = btn.closest(ITEM_SEL);
    if (item && item._matchedVariant) {
      var matched = item._matchedVariant;
      var priceEl = item.querySelector('.cts-selected-variant-price');
      if (priceEl) priceEl.textContent = formatMoney(matched.price * val);
      updateRowPrice(item, matched, val);
    }
    var section = btn.closest(SECTION_SEL);
    if (section) updateButtonTotal(section);
  }

  /* ========== Hook Theme's Add-to-Cart (fetch interceptor) ========== */

  function hookProductForm(section) {
    var _originalFetch = window.fetch;
    var _addingAddons = false;

    window.fetch = function (url, options) {
      var urlStr = (typeof url === 'string') ? url : (url && url.url ? url.url : '');

      // Only intercept POST requests to /cart/add
      if (urlStr.indexOf('/cart/add') !== -1 && options && options.method &&
          options.method.toUpperCase() === 'POST') {

        var addons = getCheckedAddons(section);
        if (addons.length > 0 && !_addingAddons) {
          // Guard against concurrent add-to-cart (e.g. rapid taps on mobile)
          _addingAddons = true;
          // Strategy: add addon items FIRST, then let the theme's original
          // request go through. The theme uses FormData + Section Rendering API,
          // so the response sections will include addons already in the cart.
          return _originalFetch.call(window, '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: addons })
          }).then(function () {
            // Addons added — now let the theme's original request proceed
            return _originalFetch.apply(window, [url, options]);
          }).catch(function () {
            // If addon add fails, still proceed with the theme's request
            return _originalFetch.apply(window, [url, options]);
          }).then(function (res) {
            _addingAddons = false;
            return res;
          }, function (err) {
            _addingAddons = false;
            throw err;
          });
        }
      }

      return _originalFetch.apply(window, arguments);
    };
  }

  function getCheckedAddons(section) {
    // Add-ons scale with the main product quantity (e.g. 2 bottles -> 2 of each add-on).
    var mainQty = getMainQty();
    var addons = [];
    section.querySelectorAll(ITEM_SEL + '[data-checked="true"]:not([data-sold-out="true"])').forEach(function (item) {
      var variantId = item.dataset.selectedVariantId;
      var qtyInput = item.querySelector('.cts-qty-value');
      var perItem = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;

      if (variantId) {
        var addon = { id: parseInt(variantId, 10), quantity: perItem * mainQty };
        var poProps = getPreorderProps(item, variantId);
        if (poProps) addon.properties = poProps;
        addons.push(addon);
      }
    });
    return addons;
  }

  /* ========== Trigger Theme Cart ========== */

  function triggerCartUpdate() {
    document.dispatchEvent(new CustomEvent('cart:refresh'));

    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        document.querySelectorAll(
          '.cart-count-bubble span, .cart-count, [data-cart-count], .js-cart-count'
        ).forEach(function (el) {
          el.textContent = cart.item_count;
        });
        document.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: cart } }));
      })
      .catch(function () {});

    var cartDrawer = document.querySelector('cart-drawer') || document.querySelector('#cart-drawer');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
    } else if (cartDrawer) {
      var cartIcon = document.querySelector('[data-cart-toggle], .js-cart-toggle, a[href="/cart"]');
      if (cartIcon) cartIcon.click();
    }
  }

  /* ========== Image zoom lightbox ========== */

  // Bump the Shopify CDN width param so the zoom is hi-res (capped at source size).
  function hiRes(src) {
    if (!src) return src;
    if (/[?&]width=\d+/.test(src)) return src.replace(/([?&]width=)\d+/, '$11400');
    return src + (src.indexOf('?') >= 0 ? '&' : '?') + 'width=1400';
  }

  function openZoom(src, alt) {
    if (document.querySelector('.cts-zoom-overlay')) return;
    var ov = document.createElement('div');
    ov.className = 'cts-zoom-overlay';
    ov.innerHTML =
      '<button type="button" class="cts-zoom-close" aria-label="Close">&times;</button>' +
      '<img src="' + hiRes(src) + '" alt="' + (alt || '').replace(/"/g, '&quot;') + '">';
    document.body.appendChild(ov);
    document.body.style.overflow = 'hidden';
    function close() {
      ov.remove();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKey);
    }
    ov.addEventListener('click', function (e) {
      if (e.target === ov || e.target.closest('.cts-zoom-close')) close();
    });
    function onKey(e) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
  }

  /* ========== Helpers ========== */

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /* ========== Global qty callback for inline onclick handlers ========== */

  window._ctsQtyUpdate = function (btn) {
    var item = btn.closest(ITEM_SEL);
    if (item && item._matchedVariant) {
      var qtyInput = item.querySelector('.cts-qty-value');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
      var matched = item._matchedVariant;
      var priceEl = item.querySelector('.cts-selected-variant-price');
      if (priceEl) priceEl.textContent = formatMoney(matched.price * qty);
      updateRowPrice(item, matched, qty);
    }
    var section = btn.closest(SECTION_SEL);
    if (section) updateButtonTotal(section);
  };

  /* ========== Add-to-Cart Button Total ========== */

  var _submitBtn = null;
  var _submitBtnText = null;
  var _originalBtnLabel = '';
  var _mainPrice = 0;
  var _mainVariantPrices = {}; // { variantId: price } for the main product
  var _mainVariantOptions = {}; // { variantId: [option1, option2, option3] }

  function initButtonTotal(section) {
    // Find the theme's add-to-cart button text element (multiple selectors for compatibility)
    _submitBtnText = document.querySelector('#AddToCart .single-add-to-cart-button--text')
      || document.querySelector('form[action="/cart/add"] .single-add-to-cart-button--text')
      || document.querySelector('[data-type="add-to-cart-form"] .single-add-to-cart-button--text')
      || document.querySelector('.single-add-to-cart-button--text');
    _submitBtn = _submitBtnText ? _submitBtnText.closest('button') : null;
    if (_submitBtnText) {
      _originalBtnLabel = _submitBtnText.textContent.trim();
    }

    // Get main product price from section data attribute (initial value)
    _mainPrice = parseInt(section.dataset.mainVariantPrice, 10) || 0;

    // Fetch the main product's variants JSON once so we can resolve the
    // current price directly from the [name="id"] input — works on every
    // theme regardless of which custom event the variant picker fires.
    var handle = getMainProductHandle();
    if (handle) {
      fetch('/products/' + handle + '.js')
        .then(function (res) { return res.json(); })
        .then(function (product) {
          product.variants.forEach(function (v) {
            _mainVariantPrices[v.id] = v.price;
            _mainVariantOptions[v.id] = [v.option1, v.option2, v.option3].filter(function (o) { return o != null; });
          });
          syncMainPriceFromForm();
          updateButtonTotal(section);
        })
        .catch(function () {});
    }

    // Listen for theme variant changes (multiple event names for compatibility)
    var variantEvents = [
      'product:variant-change',
      'variant:change',
      'on:variant:change',
      'variantChange'
    ];
    variantEvents.forEach(function (evt) {
      document.addEventListener(evt, function (e) {
        var v = e.detail && (e.detail.variant || e.detail);
        if (v && v.price) {
          _mainPrice = v.price;
        } else {
          syncMainPriceFromForm();
        }
        updateButtonTotal(section);
      });
    });

    // Document-level change listener catches variant radios, variant selects,
    // and quantity inputs regardless of where they sit in the DOM. Many themes
    // (Dawn-style) put inputs outside the <form> and associate via form="id".
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.name === 'quantity' || t.name === 'id' || t.name === 'Qty' ||
          t.closest && (t.closest('variant-selects') || t.closest('variant-radios'))) {
        syncMainPriceFromForm();
        updateButtonTotal(section);
      }
    });

    document.addEventListener('input', function (e) {
      if (e.target && e.target.name === 'quantity') {
        updateButtonTotal(section);
      }
    });

    // Delegated click handler — catches +/- qty buttons (e.g. <quantity-selector>
    // .minus / .plus) and any variant picker labels. Defer so the theme's own
    // handler runs first and updates state.
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (t.closest('quantity-selector') || t.closest('.quantity') ||
          t.closest('variant-selects') || t.closest('variant-radios') ||
          t.closest('.product-form__input')) {
        setTimeout(function () {
          syncMainPriceFromForm();
          updateButtonTotal(section);
        }, 60);
      }
    });

    // URL-based variant sync: Shopify themes update ?variant= on selection.
    window.addEventListener('popstate', function () {
      syncMainPriceFromForm();
      updateButtonTotal(section);
    });

    // Last-resort polling: covers themes that set .value directly via JS with
    // no event. Polls qty + URL variant param. Stops after 30 min.
    var lastQty = null;
    var lastVariant = null;
    var pollStart = Date.now();
    var pollInterval = setInterval(function () {
      if (Date.now() - pollStart > 30 * 60 * 1000) {
        clearInterval(pollInterval);
        return;
      }
      var q = getMainQty();
      var v = getUrlVariantId();
      if (q !== lastQty || v !== lastVariant) {
        lastQty = q;
        lastVariant = v;
        syncMainPriceFromForm();
        updateButtonTotal(section);
      }
    }, 300);

    updateButtonTotal(section);
  }

  function getMainQtyInput() {
    // Main product's quantity input — search globally since it may live
    // outside the <form> and associate via form="..." attribute.
    return document.querySelector('quantity-selector input[name="quantity"]')
      || document.querySelector('[id^="quantity-template"] input[name="quantity"]')
      || document.querySelector('input[name="quantity"]:not(.cts-qty-value)');
  }

  function getMainQty() {
    var input = getMainQtyInput();
    return input ? (parseInt(input.value, 10) || 1) : 1;
  }

  function getUrlVariantId() {
    try {
      var p = new URLSearchParams(window.location.search);
      return parseInt(p.get('variant'), 10) || null;
    } catch (e) { return null; }
  }

  function getMainProductHandle() {
    var m = window.location.pathname.match(/\/products\/([^\/\?#]+)/);
    return m ? m[1] : null;
  }

  function syncMainPriceFromForm() {
    // Try multiple signals to find the currently selected main variant:
    // 1. Enabled [name="id"] input anywhere on the page
    // 2. URL ?variant= param (themes that update URL on variant select)
    // 3. Selected radio inside <variant-selects> / <variant-radios>
    // 4. Selected <option> in a variant <select>
    var vid = null;

    var idInputs = document.querySelectorAll('input[name="id"]:not([disabled]), select[name="id"]:not([disabled])');
    for (var i = 0; i < idInputs.length; i++) {
      var v = parseInt(idInputs[i].value, 10);
      if (v && _mainVariantPrices[v] != null) { vid = v; break; }
    }

    if (!vid) {
      var urlVid = getUrlVariantId();
      if (urlVid && _mainVariantPrices[urlVid] != null) vid = urlVid;
    }

    if (!vid) {
      // Look up via option label → variant match using the embedded JSON
      var selectedRadios = document.querySelectorAll('variant-selects input[type="radio"]:checked, variant-radios input[type="radio"]:checked');
      if (selectedRadios.length > 0) {
        var selectedValues = [];
        selectedRadios.forEach(function (r) { selectedValues.push(r.value); });
        if (_mainVariantOptions) {
          for (var id in _mainVariantOptions) {
            var opts = _mainVariantOptions[id];
            var match = opts.every(function (o) { return selectedValues.indexOf(o) !== -1; });
            if (match && _mainVariantPrices[id] != null) { vid = parseInt(id, 10); break; }
          }
        }
      }
    }

    if (vid && _mainVariantPrices[vid] != null) {
      _mainPrice = _mainVariantPrices[vid];
    }
  }

  function getProductForm() {
    return document.querySelector('[data-type="add-to-cart-form"]')
      || document.querySelector('form[action="/cart/add"]');
  }

  function updateButtonTotal(section) {
    // Sum checked add-on line totals (cents) — used by both the native theme
    // button and the broadcast below.
    var addonsCents = 0;
    section.querySelectorAll(ITEM_SEL + '[data-checked="true"]:not([data-sold-out="true"])').forEach(function (item) {
      if (item._matchedVariant) {
        var qtyInput = item.querySelector('.cts-qty-value');
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;
        addonsCents += item._matchedVariant.price * qty;
      }
    });

    // Broadcast for custom buy-boxes that own their own button (e.g. the pet-jar
    // section), which fold this subtotal into their "Add to cart" total themselves.
    try {
      document.dispatchEvent(new CustomEvent('cts:addons-change', { detail: { addonsCents: addonsCents } }));
    } catch (e) {}

    // Native theme button text — only when such a button exists and is enabled.
    if (!_submitBtnText) return;
    if (_submitBtn && _submitBtn.disabled) return;
    var mainQty = getMainQty();
    _submitBtnText.textContent = formatMoney(_mainPrice * mainQty + addonsCents) + ' — ' + _originalBtnLabel;
  }

  /* ========== Boot ========== */

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
